import React, { useContext, useRef, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import BellIcon from "../assets/icons/sidebarIcons/bell.svg";
import PhoneIcon from "../assets/icons/sidebarIcons/phone.svg";
import UserIcon from "../assets/icons/sidebarIcons/aboutUs.svg";
import CartIcon from "../assets/icons/sidebarIcons/cart.svg";
import LogoPic from "../assets/images/Logo.png";
import ShopName from "../assets/icons/sidebarIcons/ShopNameWhite.svg";
import { UserContext } from "../pages/UserContext";
import profile from "../assets/icons/footerIcons/profile.svg";
import ShopNameSVG from "./ShopNameSVG";
import Loyalty from "../assets/icons/footerIcons/loyalty.svg";
import Rate from "../assets/icons/footerIcons/rate.svg";

const Sidebar = ({ isOpen, onClose, userName }) => {
  const { logout, user } = useContext(UserContext);
  const navigate = useNavigate();
  const sidebarRef = useRef(null);

  const handleLogout = () => {
    logout(); // Clear user credentials
    onClose();
    navigate("/login"); // Redirect to login page
  };

  const [settings, setSettings] = useState({
    shopName: "Shop Name",
    shopLogo: "",
    whatsappNumber: "",
  });

  useEffect(() => {
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/settings`, {
      method: "GET",
      headers: { "Content-Type": "application/json" },
    })
      .then((res) => res.json())
      .then((data) => {
        if (data) {
          setSettings({
            shopName: data.shop_name || "Shop Name",
            shopLogo: data.logo_white || "/assets/images/default-logo.png", // Default logo fallback
            whatsappNumber: data.whatsapp_number || "",
          });
        }
      })
      .catch((error) => {
        console.error("Error fetching settings:", error);
      });
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      // Check if the click is outside the sidebar
      if (isOpen && sidebarRef.current && !sidebarRef.current.contains(event.target)) {
        event.stopPropagation(); // Prevent any other actions
        onClose(); // Call onClose to close the sidebar
      }
    };

    // Add event listener to detect clicks
    document.addEventListener("mousedown", handleClickOutside);

    // Cleanup event listener on component unmount
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen, onClose]);

  return (
    <>
      {isOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-10"
          onClick={(e) => e.stopPropagation()} // Prevent overlay from triggering other actions
        >
          <div
            ref={sidebarRef}
            className={`fixed top-0 left-0 h-full w-64 bg-gray-100 shadow-lg transform ${
              isOpen ? "translate-x-0" : "-translate-x-full"
            } transition-transform duration-300 ease-in-out z-20 flex flex-col overflow-hidden`}
          >
            {/* Scrollable Content */}
            <div className="flex-1 overflow-y-auto">
              {/* Sidebar Header */}
              <div className="flex flex-col">
                <div className="bg-[rgb(89,10,10)] text-white p-1 relative">
                  <button className="text-white text-2xl absolute pl-2" onClick={onClose}>
                    ×
                  </button>
                  <div className="flex grid-cols-2 p-4 items-center mt-4">
                    <img src={settings.shopLogo} alt="Shop Logo" className="h-20 w-15 object-contain" />
                    <ShopNameSVG shopName={settings.shopName} colour="white" />
                  </div>
                  <Link to="/profile" onClick={onClose}>
                    <div className="flex justify-left">
                      <div className="flex flex-raw bg-white space-x-6 rounded-lg px-4 py-1 shadow-md">
                        <div className="text-sm text-black text-center">{userName}</div>
                        <img
                          src={user.profile_picture || profile}
                          alt="User Profile"
                          className="h-6 w-6 object-cover rounded-full border border-gray-300"
                        />
                      </div>
                    </div>
                  </Link>
                </div>

                {/* Menu Items */}
                <div className="p-4 space-y-6">
                <Link to="/home" onClick={onClose}>
                    <div className="flex items-center space-x-5 mb-5">
                      <img src={BellIcon} alt="Notification" className="h-6 w-6" />
                      <span className="text-black font-normal">Home</span>
                    </div>
                  </Link>
                  <Link to="/rate" onClick={onClose}>
                    <div className="flex items-center space-x-5 mb-5">
                      <img src={Rate} alt="Gold Rate" className="h-6 w-6" />
                      <span className="text-black font-normal">Gold Rate</span>
                    </div>
                  </Link>
                  <Link to="/loyalty" onClick={onClose}>
                    <div className="flex items-center space-x-5 mb-5">
                      <img src={Loyalty} alt="Loyalty" className="h-6 w-6" />
                      <span className="text-black font-normal">Loyalty</span>
                    </div>
                  </Link>
                  <Link to="/cart" onClick={onClose}>
                    <div className="flex items-center space-x-5 mb-5">
                      <img src={CartIcon} alt="Cart" className="h-6 w-6" />
                      <span className="text-black font-normal">Cart</span>
                    </div>
                  </Link>
                  <Link to="/notifications" onClick={onClose}>
                    <div className="flex items-center space-x-5 mb-5">
                      <img src={BellIcon} alt="Notification" className="h-6 w-6" />
                      <span className="text-black font-normal">Notification</span>
                    </div>
                  </Link>   
                  <Link to="/about-us" onClick={onClose}>
                    <div className="flex items-center space-x-5 mb-5">
                      <img src={UserIcon} alt="About us" className="h-6 w-6" />
                      <span className="text-black font-normal">About us</span>
                    </div>
                  </Link>

                  <Link to="/contact-us" onClick={onClose}>
                    <div className="flex items-center space-x-5 mb-5">
                      <img src={PhoneIcon} alt="Contact us" className="h-6 w-6" />
                      <span className="text-black font-normal">Contact us</span>
                    </div>
                  </Link>
                </div>
              </div>
            </div>

            {/* Logout Button (fixed at the bottom) */}
            <div className="p-4 bg-gray-200">
              <button
                onClick={handleLogout}
                className="w-full bg-[rgb(89,10,10)] text-white p-2 rounded-md flex items-center justify-center space-x-2"
              >
                <span>Logout</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Sidebar;
